import { Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { SubBadge } from "@/components/admin/SectionShell";
import { Switch } from "@/components/ui/switch";
import { resolveImg } from "@/lib/media";
import { SectionCard } from "../SectionShell";
import { inputCls, TextField, ToggleRow } from "./Fields";
import { HowToPicker } from "./HowToPicker";
import { ParticipantDemosAdmin } from "./ParticipantDemosAdmin";

const ProgramEditor = ({ p, i, setProg, removeProgram, imgUploading, uploadProgramImage }) => (
  <div className="rounded-2xl border border-border bg-white/60 p-5" data-testid={`program-editor-${i}`}>
    <div className="flex items-center justify-between mb-4">
      <p className="font-medium"><SubBadge /> Program {i + 1}</p>
      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <span className="text-xs text-muted-foreground">Register link</span>
          <Switch data-testid={`program-reg-toggle-${i}`} checked={!!p.register_enabled} onCheckedChange={(v) => setProg(i, "register_enabled", v)} />
        </label>
        <button type="button" data-testid={`program-remove-${i}`} onClick={() => removeProgram(i)} title="Remove program" className="grid place-items-center w-8 h-8 rounded-full border border-destructive/40 text-destructive hover:bg-destructive hover:text-white transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
    <label className="text-xs uppercase tracking-widest text-muted-foreground">Program {i + 1} name</label>
    <input data-testid={`content-progname-${i}`} value={p.name || ""} onChange={(e) => setProg(i, "name", e.target.value)} className={`${inputCls} mb-4`} />
    <label className="text-xs uppercase tracking-widest text-muted-foreground">Program {i + 1} description</label>
    <textarea data-testid={`content-progdesc-${i}`} rows={2} value={p.desc || ""} onChange={(e) => setProg(i, "desc", e.target.value)} className={`${inputCls} mb-4 resize-none`} />
    <label className="text-xs uppercase tracking-widest text-muted-foreground">Program {i + 1} image</label>
    <div className="flex flex-wrap gap-3 items-center mt-2">
      <img src={resolveImg(p.image)} alt="" className="w-14 h-14 rounded-lg object-cover border border-border bg-muted" />
      <input data-testid={`content-img-${i}`} value={p.image || ""} onChange={(e) => setProg(i, "image", e.target.value)} placeholder="Paste image URL or upload →" className={`flex-1 min-w-[200px] ${inputCls} mt-0 text-sm`} />
      <label data-testid={`content-img-upload-label-${i}`} className={`inline-flex items-center gap-2 rounded-full border border-accent text-accent px-4 py-2.5 text-sm cursor-pointer hover:bg-accent hover:text-white transition-colors ${imgUploading === i ? "opacity-60 pointer-events-none" : ""}`}>
        <ImageIcon size={15} /> {imgUploading === i ? "Uploading…" : "Upload"}
        <input type="file" accept="image/*" data-testid={`content-img-upload-${i}`} className="hidden" onChange={(e) => uploadProgramImage(i, e.target.files?.[0])} />
      </label>
    </div>
    {i === 0 && <p className="text-xs text-accent mt-2">This image is shown on all program cards.</p>}
  </div>
);

const ChecklistManager = ({ content, setC, setContent }) => {
  const qs = content.checklist_questions || [];
  const update = (fn) => setContent((c) => ({ ...c, checklist_questions: fn([...(c.checklist_questions || [])]) }));
  return (
    <div className="mt-6 rounded-2xl border border-[#E2DCF2] bg-[#F4F1FC] p-5" data-testid="checklist-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-medium"><SubBadge /> Registration checklist</p>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid="content-checklist-enabled" checked={content.checklist_enabled !== false} onChange={(e) => setC("checklist_enabled", e.target.checked)} className="w-4 h-4 accent-[#1A2E26]" />
          Show checklist before form
        </label>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <TextField k="checklist_title" label="Checklist title" content={content} setC={setC} />
        <TextField k="checklist_subtitle" label="Checklist subtitle" content={content} setC={setC} />
      </div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground">Questions</label>
      <div className="space-y-2 mt-2">
        {qs.map((q, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`checklist-q-${i}`}>
            <span className="text-xs text-muted-foreground w-5 shrink-0">{i + 1}.</span>
            <input value={q} onChange={(e) => update((n) => { n[i] = e.target.value; return n; })} className="flex-1 bg-white/70 border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-accent transition-colors" />
            <button type="button" data-testid={`checklist-q-up-${i}`} disabled={i === 0} onClick={() => update((n) => { [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })} className="grid place-items-center w-8 h-8 rounded-full border border-border hover:bg-muted transition-colors disabled:opacity-30" title="Move up">↑</button>
            <button type="button" data-testid={`checklist-q-down-${i}`} disabled={i === qs.length - 1} onClick={() => update((n) => { [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })} className="grid place-items-center w-8 h-8 rounded-full border border-border hover:bg-muted transition-colors disabled:opacity-30" title="Move down">↓</button>
            <button type="button" data-testid={`checklist-q-remove-${i}`} onClick={() => update((n) => n.filter((_, x) => x !== i))} className="grid place-items-center w-8 h-8 rounded-full border border-destructive/40 text-destructive hover:bg-destructive hover:text-white transition-colors" title="Remove"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" data-testid="checklist-q-add" onClick={() => update((n) => [...n, ""])} className="mt-3 inline-flex items-center gap-2 rounded-full border border-accent text-accent px-4 py-2 text-sm hover:bg-accent hover:text-white transition-colors">
        <Plus size={15} /> Add question
      </button>
    </div>
  );
};

export const ProgramsSection = ({ content, setC, setContent, setProg, addProgram, removeProgram, imgUploading, uploadProgramImage, authFetch }) => (
  <SectionCard id="programs" testId="section-our-programs">
    <ToggleRow testId="content-registration-visible" label="Registration enabled (master)" sub="Turns the Register links on every program card on/off" checked={content.registration_visible !== false} onChange={(v) => setC("registration_visible", v)} className="mb-5" />
    <div className="grid sm:grid-cols-2 gap-4 mb-5">
      <TextField k="batch_capacity" label="Batch capacity (1st program)" content={content} setC={setC} />
      <div>
        <label className="text-xs uppercase tracking-widest text-muted-foreground">"Few seats left" warning threshold (%)</label>
        <input type="number" min="1" max="100" data-testid="content-few_left_pct" value={content.few_left_pct ?? 80} onChange={(e) => setC("few_left_pct", e.target.value)} className={inputCls} />
      </div>
    </div>
    <div data-testid="programs-section">
      <div className="flex items-center justify-between mb-4">
        <p className="font-medium"><SubBadge /> Programs ({(content.programs || []).length})</p>
        <button type="button" data-testid="add-program-btn" onClick={addProgram} className="inline-flex items-center gap-2 rounded-full border border-accent text-accent px-4 py-2 text-sm hover:bg-accent hover:text-white transition-colors">
          <Plus size={15} /> Add program
        </button>
      </div>
      <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2" data-testid="programs-list">
        {(content.programs || []).map((p, i) => (
          <ProgramEditor key={i} p={p} i={i} setProg={setProg} removeProgram={removeProgram} imgUploading={imgUploading} uploadProgramImage={uploadProgramImage} />
        ))}
      </div>
    </div>
    <ChecklistManager content={content} setC={setC} setContent={setContent} />
  </SectionCard>
);

export const DemoSection = ({ content, setC, authFetch }) => (
  <SectionCard id="demo" testId="demo-section">
    <HowToPicker content={content} setC={setC} authFetch={authFetch} />
    <ParticipantDemosAdmin authFetch={authFetch} />
  </SectionCard>
);
